import React, { useState } from 'react';
import { useTeam } from '../../context/TeamContext';
import { useProducts } from '../../context/ProductsContext';
import { useCalculator } from '../../context/CalculatorContext';
import { calculateTeamFTE, generateTeamJustification } from '../../utils/teamCalculations';
import Card from '../Common/Card';
import Button from '../Common/Button';
import Input from '../Common/Input';
import { useToast } from '../Common/Toast';

const DirectorView = () => {
  const { teams, addTeam, removeTeam } = useTeam();
  const { products } = useProducts();
  const { inputs } = useCalculator();
  const { showToast } = useToast();
  
  const [showCreateTeam, setShowCreateTeam] = useState(false);
  const [teamName, setTeamName] = useState('');
  const [directorName, setDirectorName] = useState('');
  const [parentTeamId, setParentTeamId] = useState('');

  // Get all director, AVP, and manager teams with type checking
  const directorTeams = Array.isArray(teams) ? teams.filter(t => t.role === 'director') : [];
  const avpTeams = Array.isArray(teams) ? teams.filter(t => t.role === 'avp') : [];
  
  // Directors can only see manager teams that report to their director teams
  const myDirectorIds = directorTeams.map(d => d.id);
  const managerTeams = Array.isArray(teams) ? teams.filter(t => 
    t.role === 'manager' && myDirectorIds.includes(t.parentTeamId)
  ) : [];

  const handleCreateTeam = async () => {
    if (!teamName || !directorName) {
      showToast('Please enter team name and director name', 'error');
      return;
    }

    const result = await addTeam({
      name: teamName,
      managerName: directorName,
      managerEmail: '',
      role: 'director',
      parentTeamId: parentTeamId || null,
      activities: []
    });

    if (result.success) {
      showToast('Director team created successfully!');
      setShowCreateTeam(false);
      setTeamName('');
      setDirectorName('');
      setParentTeamId('');
    } else {
      showToast('Failed to create team', 'error');
    }
  };

  const handleDeleteTeam = async (teamId, teamName) => {
    if (window.confirm(`Are you sure you want to delete "${teamName}"? This will not delete manager teams under it, but they will become independent.`)) {
      const result = await removeTeam(teamId);
      if (result.success) {
        showToast('Director team deleted successfully!');
      } else {
        showToast('Failed to delete team', 'error');
      }
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">📊 Director View</h2>
        <Button onClick={() => setShowCreateTeam(true)} variant="primary">
          + Create New Director Team
        </Button>
      </div>

      {/* Create Team Modal */}
      {showCreateTeam && (
        <Card title="Create Director Team">
          <div className="space-y-4">
            <Input
              label="Team Name"
              value={teamName}
              onChange={(e) => setTeamName(e.target.value)}
              placeholder="e.g., Operations EHS"
            />
            <Input
              label="Director Name"
              value={directorName}
              onChange={(e) => setDirectorName(e.target.value)}
              placeholder="Your name"
            />
            {avpTeams.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reports To (AVP)
                </label>
                <select
                  value={parentTeamId}
                  onChange={(e) => setParentTeamId(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">-- No AVP (Independent) --</option>
                  {avpTeams.map(team => (
                    <option key={team.id} value={team.id}>
                      {team.name} - {team.managerName}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="flex gap-3">
              <Button onClick={handleCreateTeam} variant="primary">
                Create Team
              </Button>
              <Button onClick={() => setShowCreateTeam(false)} variant="outline">
                Cancel
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Director Teams List */}
      {directorTeams.length > 0 ? (
        <div className="space-y-4">
          {directorTeams.map(directorTeam => {
            // Get manager teams under this director
            const teamManagers = managerTeams.filter(m => m.parentTeamId === directorTeam.id);
            
            // Calculate rollup totals
            const totalFTE = teamManagers.reduce((sum, team) => {
              const teamActivities = products.filter(p => team.activities && team.activities.includes(p.id));
              return sum + calculateTeamFTE(teamActivities, inputs);
            }, 0);
            
            const totalActivities = teamManagers.reduce((sum, team) => sum + (team.activities?.length || 0), 0);
            const avpTeam = avpTeams.find(a => a.id === directorTeam.parentTeamId);

            return (
              <Card key={directorTeam.id} title={
                <div className="flex justify-between items-center">
                  <span>Director Team: {directorTeam.name}</span>
                  <button
                    onClick={() => handleDeleteTeam(directorTeam.id, directorTeam.name)}
                    className="text-red-600 hover:text-red-800 text-sm px-3 py-1 rounded border border-red-300 hover:bg-red-50"
                  >
                    🗑️ Delete Team
                  </button>
                </div>
              }>
                <div className="space-y-4">
                  {/* Director Info */}
                  <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-4 rounded-lg">
                    <div className="grid grid-cols-4 gap-4">
                      <div>
                        <span className="text-xs text-gray-600">Director</span>
                        <div className="font-semibold text-gray-900">{directorTeam.managerName}</div>
                      </div>
                      <div>
                        <span className="text-xs text-gray-600">Reports To</span>
                        <div className="font-semibold text-gray-900">{avpTeam ? avpTeam.name : 'Independent'}</div>
                      </div>
                      <div>
                        <span className="text-xs text-gray-600">Manager Teams</span>
                        <div className="font-semibold text-gray-900">{teamManagers.length}</div>
                      </div>
                      <div>
                        <span className="text-xs text-gray-600">Total FTE</span>
                        <div className="font-semibold text-green-700 text-xl">{totalFTE.toFixed(2)}</div>
                      </div>
                    </div>
                  </div>

                  {/* Manager Teams Under This Director */}
                  {teamManagers.length > 0 ? (
                    <div>
                      <div className="text-sm font-semibold text-gray-900 mb-3">👥 Manager Teams ({teamManagers.length})</div>
                      <div className="space-y-3">
                        {teamManagers.map(team => {
                          const teamActivities = products.filter(p => team.activities && team.activities.includes(p.id));
                          const teamFTE = calculateTeamFTE(teamActivities, inputs);
                          const justification = generateTeamJustification(team, products, teamFTE);

                          return (
                            <div key={team.id} className="bg-white border border-gray-200 p-4 rounded-lg">
                              <div className="flex justify-between items-start mb-3">
                                <div>
                                  <div className="font-semibold text-gray-900">Manager: {team.managerName}</div>
                                  <div className="text-sm text-gray-600">Team: {team.name}</div>
                                </div>
                                <div className="text-right">
                                  <div className="text-lg font-bold text-green-700">{teamFTE.toFixed(2)} FTE</div>
                                  <div className="text-xs text-gray-600">{teamActivities.length} activities</div>
                                </div>
                              </div>

                              {/* Activities */}
                              {teamActivities.length > 0 && (
                                <div className="mb-3">
                                  <div className="text-xs font-semibold text-gray-700 mb-1">Activities:</div>
                                  <div className="flex flex-wrap gap-1">
                                    {teamActivities.map(activity => (
                                      <span key={activity.id} className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                                        {activity.name}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Justification */}
                              {justification && (
                                <div className="bg-purple-50 p-3 rounded border border-purple-200">
                                  <div className="text-xs font-semibold text-purple-900 mb-1">💼 Justification</div>
                                  <p className="text-xs text-gray-700 leading-relaxed">{justification}</p>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-6 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-600">No manager teams reporting to this director yet</p>
                    </div>
                  )}

                  {/* Director Summary */}
                  {teamManagers.length > 0 && (
                    <div className="bg-gradient-to-r from-purple-50 to-blue-50 p-4 rounded-lg border-t-2 border-purple-200">
                      <div className="text-sm font-semibold text-gray-900 mb-2">Director Summary</div>
                      <div className="grid grid-cols-3 gap-4">
                        <div className="text-center">
                          <div className="text-xl font-bold text-blue-600">{teamManagers.length}</div>
                          <div className="text-xs text-gray-600">Manager Teams</div>
                        </div>
                        <div className="text-center">
                          <div className="text-xl font-bold text-purple-600">{totalActivities}</div>
                          <div className="text-xs text-gray-600">Total Activities</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-green-700">{totalFTE.toFixed(2)}</div>
                          <div className="text-xs text-gray-600">Total FTE Required</div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      ) : (
        !showCreateTeam && (
          <Card>
            <div className="text-center py-12">
              <div className="text-6xl mb-4">📊</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No Director Teams Yet</h3>
              <p className="text-gray-600 mb-4">Create a director team to oversee manager teams and their FTE allocations.</p>
              <Button onClick={() => setShowCreateTeam(true)} variant="primary">
                Create Your First Director Team
              </Button>
            </div>
          </Card>
        )
      )}
    </div>
  );
};

export default DirectorView;
